import React, { useEffect, useMemo, useState } from 'react';
import { fetchAllContentsByPaginating } from '../services/productsService';
import {
  buildProductsByLeaf,
  buildTreeFromFlat,
  getNodeCheckState,
  setNodeChecked,
  setProductChecked,
} from '../utils/serviceAreaSelection';
import './AuthorizedAreaProductTree.css';

const catId = (c) => String(c?.id || c?._id || '');

export function AuthorizedAreaProductTree({
  categories = [],
  value,
  onChange,
  readOnly = false,
}) {
  const [expandedIds, setExpandedIds] = useState({});
  const [urunContents, setUrunContents] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const tree = useMemo(() => buildTreeFromFlat(categories), [categories]);
  const productsByLeaf = useMemo(() => buildProductsByLeaf(urunContents), [urunContents]);

  useEffect(() => {
    let cancelled = false;
    setProductsLoading(true);
    fetchAllContentsByPaginating({ icerikTipi: 'urun', aktif: true })
      .then((list) => {
        if (!cancelled) setUrunContents(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setUrunContents([]);
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleExpand = (id) => {
    setExpandedIds((p) => ({ ...p, [id]: !p[id] }));
  };

  const handleNodeCheck = (nodeId, checked) => {
    if (readOnly || !onChange) return;
    onChange(setNodeChecked(nodeId, checked, value, tree, productsByLeaf));
  };

  const handleProductCheck = (productId, leafId, checked) => {
    if (readOnly || !onChange) return;
    onChange(setProductChecked(productId, leafId, checked, value));
  };

  const renderNode = (node, depth) => {
    const cid = catId(node);
    const kids = (tree.childMap.get(cid) || []).map((id) => tree.nodesById.get(id)).filter(Boolean);
    const isLeaf = tree.leafIds.has(cid);
    const isExpanded = !!expandedIds[cid];
    const { checked, indeterminate } = getNodeCheckState(cid, value, tree, productsByLeaf);
    const label = node.kategoriAdi || node.ad || '—';
    const prods = isLeaf ? productsByLeaf.get(cid) || [] : [];

    return (
      <div key={cid} className="auth-area-tree__node">
        <div className="auth-area-tree__row" style={{ marginLeft: depth * 12 }}>
          {(!isLeaf && kids.length > 0) || (isLeaf && prods.length > 0) ? (
            <button
              type="button"
              className="auth-area-tree__expand"
              aria-expanded={isExpanded}
              onClick={() => toggleExpand(cid)}
            >
              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} aria-hidden="true" />
            </button>
          ) : (
            <span className="auth-area-tree__expand-spacer" aria-hidden="true" />
          )}
          {!readOnly ? (
            <label className="auth-area-tree__label">
              <input
                type="checkbox"
                checked={checked}
                ref={(el) => {
                  if (el) el.indeterminate = indeterminate;
                }}
                onChange={(e) => handleNodeCheck(cid, e.target.checked)}
              />
              <span>{label}</span>
            </label>
          ) : (
            <span className="auth-area-tree__label-text">{label}</span>
          )}
        </div>
        {isLeaf && isExpanded && prods.length > 0 && (
          <div className="auth-area-tree__products">
            {prods.map((p) => {
              const pid = String(p.id);
              const leafFull = (value?.leafIds || []).map(String).includes(cid);
              const prodChecked = leafFull || (value?.productIds || []).map(String).includes(pid);
              return (
                <label key={pid} className="auth-area-tree__product-row" style={{ marginLeft: depth * 12 + 28 }}>
                  {!readOnly ? (
                    <>
                      <input
                        type="checkbox"
                        checked={prodChecked}
                        onChange={(e) => handleProductCheck(pid, cid, e.target.checked)}
                      />
                      <span>{p.ad}</span>
                    </>
                  ) : (
                    <span>{p.ad}</span>
                  )}
                </label>
              );
            })}
          </div>
        )}
        {!isLeaf && isExpanded && kids.length > 0 && (
          <div>{kids.map((child) => renderNode(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  if (!tree.roots?.length) {
    return <div className="auth-area-tree__empty">Ürün kategorisi bulunamadı veya yükleniyor.</div>;
  }

  return (
    <div className="auth-area-tree">
      {productsLoading ? (
        <div className="auth-area-tree__loading">
          <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Ürünler yükleniyor…
        </div>
      ) : null}
      {tree.roots.map((root) => renderNode(root, 0))}
    </div>
  );
}
